import { salaryMultiplierLabel } from '@/features/quotes/data/group-life/capital-rules';
import { useGroupLifeCoverageSchema } from '@/features/quotes/hooks/group-life/use-coverage-schema.hook';
import { useGroupLifeProposalLives } from '../../../hooks/group-life';
import type { GroupLifeQuoteDetails } from '@/services/group-life';
import { Field, RadioCard, Tag, Text } from '@noorden-insurtech/alba-design-system';
import { useMemo, type ReactElement } from 'react';
import type { PmePlanModality, PmeProposalStepProps } from '../proposal.types';
import { resolvePlanCoverages } from '../utils/hydrate-proposal.util';
import { PmeStepFooter } from './sections/step-footer.component';

const MODALITY_LABELS: Record<PmePlanModality, string> = {
  UNIFORM: 'Uniforme',
  VARIABLE: 'Variável',
  MULTIPLE_SALARY: 'Múltiplo Salarial',
};

function UniformIcon({ className }: { className?: string }) {
  return (
    <svg
      width='20'
      height='20'
      viewBox='0 0 20 20'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <rect x='1' y='1' width='7' height='7' rx='1' stroke='currentColor' strokeWidth='2' />
      <rect x='12' y='1' width='7' height='7' rx='1' stroke='currentColor' strokeWidth='2' />
      <rect x='12' y='12' width='7' height='7' rx='1' stroke='currentColor' strokeWidth='2' />
      <rect x='1' y='12' width='7' height='7' rx='1' stroke='currentColor' strokeWidth='2' />
    </svg>
  );
}

function VariableIcon({ className }: { className?: string }) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <path
        d='M5 21V15M12 21V3M19 21V9'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function MultipleSalaryIcon({ className }: { className?: string }) {
  return (
    <svg
      width='24'
      height='24'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      className={className}
      aria-hidden='true'
    >
      <circle cx='12' cy='12' r='9' stroke='currentColor' strokeWidth='2' />
      <path
        d='M14.9992 9.00303L8.99922 15.003M8.99922 9.00303H9.00922M14.9992 15.003H15.0092'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

const MODALITY_ICONS: Record<
  PmePlanModality,
  (props: { className?: string }) => ReactElement
> = {
  UNIFORM: UniformIcon,
  VARIABLE: VariableIcon,
  MULTIPLE_SALARY: MultipleSalaryIcon,
};

type Props = PmeProposalStepProps & {
  details?: GroupLifeQuoteDetails;
  productCode?: string;
};

/**
 * Passo Plano da proposta PME — SOMENTE LEITURA. O plano (modalidade, capitais
 * e coberturas) é herdado da cotação e já está persistido no snapshot; aqui
 * apenas exibimos, resolvendo as coberturas via schema do produto (mesma fonte
 * do wizard de cotação). Avançar não re-salva o plano.
 */
export function StepPmePlan({
  onNext,
  onBack,
  canGoBack,
  canGoNext,
  saving,
  details,
  productCode,
}: Props) {
  const plan = details?.plan;
  const modality = (plan?.modality ?? 'UNIFORM') as PmePlanModality;

  const schemaQuery = useGroupLifeCoverageSchema({
    productCode: productCode || undefined,
  });

  const { total: totalLives, totalMemberCapital } = useGroupLifeProposalLives(
    details?.policyId,
    details,
  );

  // Assistência sem capital segurado não exibe valor aqui: sem o preço por
  // vida, `resolvePlanCoverages` a deixa sem valor.
  const coverageGroups = useMemo(
    () =>
      resolvePlanCoverages(schemaQuery.data, details, {
        memberCapitalOverride: totalMemberCapital,
        livesCount: totalLives,
      }),
    [schemaQuery.data, details, totalMemberCapital, totalLives],
  );

  const capitalFields = useMemo(() => {
    if (modality === 'UNIFORM') {
      return [{ label: 'Capital Uniforme', value: plan?.uniformCapital }];
    }
    const fields: { label: string; value?: string }[] = [];
    if (modality === 'MULTIPLE_SALARY') {
      fields.push({
        label: 'Quantidade de salários',
        value: plan?.numberOfSalaries
          ? salaryMultiplierLabel(plan.numberOfSalaries)
          : undefined,
      });
    }
    fields.push(
      { label: 'Capital Mínimo Individual', value: plan?.minimumIndividualCapital },
      { label: 'Capital Máximo Individual', value: plan?.maximumIndividualCapital },
    );
    return fields;
  }, [modality, plan]);

  return (
    <div className='flex flex-col gap-8'>
      <Field.Root>
        <Field.Label>Modalidade</Field.Label>
        <RadioCard.Root value={modality} disabled>
          <div className='flex flex-wrap gap-2'>
            {(Object.keys(MODALITY_LABELS) as PmePlanModality[]).map(kind => {
              const Icon = MODALITY_ICONS[kind];
              return (
                <RadioCard.Item key={kind} value={kind}>
                  <RadioCard.ItemControl className='flex-row items-center gap-2'>
                    <Icon className='h-5 w-5' />
                    <RadioCard.ItemText>{MODALITY_LABELS[kind]}</RadioCard.ItemText>
                  </RadioCard.ItemControl>
                  <RadioCard.ItemHiddenInput />
                </RadioCard.Item>
              );
            })}
          </div>
        </RadioCard.Root>
      </Field.Root>

      <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
        {capitalFields.map(field => (
          <Field.Root key={field.label}>
            <Field.Label>{field.label}</Field.Label>
            <Field.Input value={field.value || '—'} disabled />
          </Field.Root>
        ))}
      </div>

      <div className='flex flex-col gap-3'>
        <Text className='text-base font-normal text-alba-black-500'>
          Coberturas Contratadas
        </Text>

        {schemaQuery.isLoading ? (
          <div className='h-40 animate-pulse rounded-xl bg-alba-gray-200' />
        ) : schemaQuery.isError ? (
          <Text className='text-sm text-alba-error-red-500'>
            Não foi possível carregar o catálogo de coberturas.
          </Text>
        ) : coverageGroups.length === 0 ? (
          <Text className='text-sm text-alba-gray-600'>
            Nenhuma cobertura contratada nesta cotação.
          </Text>
        ) : (
          <div className='grid grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2'>
            {coverageGroups.map(group => (
              <div key={group.category} className='flex flex-col gap-3'>
                <Text className='text-base font-normal text-alba-black-500'>
                  {group.category}
                </Text>
                <div className='flex flex-col gap-4 rounded-xl border border-alba-gray-300 bg-white p-4 [&>*:not(:last-child)]:border-b [&>*:not(:last-child)]:border-alba-gray-200 [&>*:not(:last-child)]:pb-4'>
                  {group.items.map(coverage => (
                    <div
                      key={coverage.code}
                      className='flex items-center justify-between gap-4'
                    >
                      <div className='flex flex-1 items-center gap-2'>
                        <Text className='text-sm font-bold text-alba-black-500'>
                          {coverage.name}
                        </Text>
                        <Tag.Root size='sm' colorScheme='success'>
                          <Tag.Label>Contratada</Tag.Label>
                        </Tag.Root>
                      </div>
                      {coverage.capital && (
                        <Text className='shrink-0 text-sm text-alba-gray-600'>
                          {coverage.capital}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <PmeStepFooter
        onBack={onBack}
        canGoBack={canGoBack}
        onNext={() => onNext()}
        canGoNext={canGoNext && !saving}
      />
    </div>
  );
}
